import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, CircularProgress } from '@mui/joy';

interface GoogleStreetViewProps {
  location: { lat: number; lng: number };
  height?: string | number;
  heading?: number;
  pitch?: number;
  zoom?: number;
}

// Calculate bearing from one point to another (in degrees, 0-360)
const calculateBearing = (from: google.maps.LatLng, to: { lat: number; lng: number }): number => {
  const lat1 = from.lat() * Math.PI / 180;
  const lat2 = to.lat * Math.PI / 180;
  const dLng = (to.lng - from.lng()) * Math.PI / 180;

  const x = Math.sin(dLng) * Math.cos(lat2);
  const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  const bearing = Math.atan2(x, y) * 180 / Math.PI;
  return (bearing + 360) % 360; // Normalize to 0-360
};

export const GoogleStreetView: React.FC<GoogleStreetViewProps> = ({
  location,
  height = 400,
  heading = 0,
  pitch = 0,
  zoom = 1,
}) => {
  const streetViewRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!streetViewRef.current) return;

    const initStreetView = () => {
      if (!window.google || !window.google.maps) {
        setTimeout(initStreetView, 500);
        return;
      }

      try {
        // Create street view panorama
        const panorama = new google.maps.StreetViewPanorama(
          streetViewRef.current!,
          {
            position: location,
            pov: {
              heading,
              pitch,
            },
            zoom,
            addressControl: false,
            linksControl: true,
            panControl: true,
            enableCloseButton: false,
            fullscreenControl: false,
          }
        );

        panoramaRef.current = panorama;

        // Check if street view is available at this location
        const streetViewService = new google.maps.StreetViewService();
        streetViewService.getPanorama(
          {
            location,
            radius: 50, // Search within 50 meters
          },
          (data, status) => {
            if (status === google.maps.StreetViewStatus.OK && data && data.location?.latLng) {
              // Calculate heading from panorama position to property
              const panoramaPosition = data.location.latLng;
              const calculatedHeading = calculateBearing(panoramaPosition, location);

              // Update panorama to point at the property
              panorama.setPov({
                heading: calculatedHeading,
                pitch: pitch,
              });

              setLoading(false);
              setError(null);
            } else if (status === google.maps.StreetViewStatus.OK && data) {
              // Fallback if latLng not available
              setLoading(false);
              setError(null);
            } else {
              setLoading(false);
              setError('Street View is not available at this location');
            }
          }
        );
      } catch (err) {
        console.error('Error initializing Street View:', err);
        setLoading(false);
        setError('Failed to initialize Street View');
      }
    };

    initStreetView();

    return () => {
      panoramaRef.current = null;
    };
  }, [location, heading, pitch, zoom]);

  return (
    <Box sx={{ position: 'relative', height, width: '100%' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Alert color="neutral" sx={{ position: 'absolute', top: 10, left: 10, right: 10, zIndex: 1 }}>
          {error}
        </Alert>
      )}
      
      <Box
        ref={streetViewRef}
        sx={{
          height: '100%',
          width: '100%',
          borderRadius: 'sm',
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};